import type { FC, ReactNode } from "react";

import classNames from "classnames";

import styles from "./Table.module.scss";

export type TableRow = {
  [key: string]: ReactNode;
};

export type TableColumn = {
  key: string;
  label: ReactNode;
  className?: string;
  render?: (value: ReactNode, row: TableRow, index: number) => ReactNode;
};

export type TableProps = {
  columns: TableColumn[];
  rows: TableRow[];
  className?: string;
  headingClassName?: string;
  testId?: string;
};

export const Table: FC<TableProps> = ({ columns, rows, className, headingClassName, testId = "table" }) => {
  return (
    <table data-testid={testId} className={classNames(styles.table, className)}>
      <thead className={classNames(styles.headingWrapper, headingClassName)}>
        <tr data-testid={`${testId}-header`} className={styles.tableHeadingRow}>
          {columns.map((column, index) => (
            <th
              key={`${column.key}-${index}`}
              className={classNames(
                styles.headingCell,
                { [styles.lastColumn]: index === columns.length - 1 },
                column.className
              )}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex} data-testid={`${testId}-row`} className={styles.tableRow}>
            {columns.map((column, colIndex) => {
              const value = row[column.key];
              const cellContent = column.render ? column.render(value, row, rowIndex) : value;
              const isLastColumn = colIndex === columns.length - 1;

              return (
                <td
                  key={`${column.key}-${colIndex}`}
                  className={classNames(styles.tableCell, { [styles.lastColumn]: isLastColumn }, column.className)}
                >
                  {cellContent}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
